import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Brain, FileText, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export type AIProcessingPhase = 'uploading' | 'extracting' | 'complete';

interface AIDocumentScanLoaderProps {
  phase: AIProcessingPhase;
  previewUrl?: string;
  previewMime?: string;
  onDone?: () => void;
}

const COMPLETE_FLASH_MS = 600;
const EXTRACT_CYCLE_MS = 1500;

/**
 * Compact inline AI scanning loader — lives inside the upload dropzone so it
 * stays visible on mobile without scrolling.
 */
const AIDocumentScanLoader: React.FC<AIDocumentScanLoaderProps> = ({
  phase,
  previewUrl,
  previewMime,
  onDone,
}) => {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();
  const [extractToggle, setExtractToggle] = useState(false);

  useEffect(() => {
    if (phase !== 'extracting') {
      setExtractToggle(false);
      return;
    }
    const id = window.setInterval(() => setExtractToggle((v) => !v), EXTRACT_CYCLE_MS);
    return () => window.clearInterval(id);
  }, [phase]);

  useEffect(() => {
    if (phase !== 'complete') return;
    const id = window.setTimeout(() => onDone?.(), COMPLETE_FLASH_MS);
    return () => window.clearTimeout(id);
  }, [phase, onDone]);

  const statusKey =
    phase === 'uploading'
      ? 'documents.aiLoader.uploading'
      : phase === 'extracting'
        ? extractToggle
          ? 'documents.aiLoader.validating'
          : 'documents.aiLoader.extracting'
        : 'documents.aiLoader.ready';

  const progress =
    phase === 'uploading' ? 30 : phase === 'extracting' ? 72 : 100;

  const isImage = Boolean(previewUrl) && !!previewMime?.startsWith('image/');

  return (
    <motion.div
      role="status"
      aria-live="polite"
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.98 }}
      transition={{ duration: 0.25 }}
      className="flex w-full items-center gap-3 sm:gap-4 rounded-xl border border-gray-200 bg-gray-50 p-3 sm:p-4"
    >
      <div className="relative flex-shrink-0 w-14 h-[4.5rem] sm:w-16 sm:h-20 rounded-lg overflow-hidden border border-white shadow-sm bg-white">
        {isImage ? (
          <img src={previewUrl} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-1 bg-gray-50 text-gray-500">
            <FileText size={22} />
          </div>
        )}

        {!reduceMotion && phase !== 'complete' && (
          <motion.div
            aria-hidden
            className="absolute inset-x-0 h-0.5 bg-gradient-to-r from-transparent via-gray-400 to-transparent shadow-[0_0_6px_rgba(107,114,128,0.6)]"
            animate={{ top: ['0%', '100%', '0%'] }}
            transition={{ duration: 2.2, ease: 'easeInOut', repeat: Infinity }}
          />
        )}

        {phase === 'complete' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute inset-0 bg-emerald-500/20 flex items-center justify-center"
          >
            <Sparkles size={18} className="text-emerald-600" />
          </motion.div>
        )}
      </div>

      <div className="flex-1 min-w-0 flex flex-col gap-2">
        <div className="flex items-center gap-1.5">
          <span className="flex-shrink-0 w-6 h-6 rounded-md bg-white border border-gray-200 text-gray-600 flex items-center justify-center">
            {reduceMotion ? (
              <Sparkles size={12} />
            ) : (
              <motion.span
                animate={{ rotate: [0, 8, -8, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              >
                <Brain size={13} />
              </motion.span>
            )}
          </span>
          <span className="text-xs sm:text-sm font-semibold text-gray-800 truncate">
            {t('documents.aiLoader.title')}
          </span>
        </div>

        <div className="h-4 relative overflow-hidden">
          <AnimatePresence mode="wait" initial={false}>
            <motion.p
              key={statusKey}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.2 }}
              className="text-[11px] sm:text-xs text-gray-600 leading-snug truncate"
            >
              {t(statusKey)}
            </motion.p>
          </AnimatePresence>
        </div>

        <div className="h-1 rounded-full bg-gray-200/80 overflow-hidden">
          <motion.div
            className="h-full rounded-full bg-haat-red-500"
            initial={{ width: '0%' }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          />
        </div>
      </div>
    </motion.div>
  );
};

export default AIDocumentScanLoader;
